import {
  compactFormat,
  dataLabelsCompact,
  dataLabelsDecimals,
  dataLabelsFlipHorizontally,
  dataLabelsHideThreshold,
  dataLabelsSizes,
  granularity,
  legendPosition,
  palette,
  showDataLabels,
} from "@/settings/charts/settingsDictionary";
import { FAMILY } from "@/settings/widgetFamilies";
import { Config } from "@/types/charts";

const decompositionMetric = {
  default: "month",
  type: "select",
  label: "Horizontal Axis (Date)",
  dictionary: "timeMetrics",
  apiParameter: "dimension",
  gridPosition: [0, 0],
  tooltipOrder: 0,
};

const decompositionMetricSecondary = {
  default: "channel",
  type: "select",
  label: "Decomposition Metric (Stacked Bars)",
  dictionary: "dimensionMetrics",
  apiParameter: "dimension",
  gridPosition: [1, 0],
  tooltipOrder: 1,
};

const lineMetric = {
  default: "impressions",
  type: "select",
  label: "Display Metric (Line)",
  dictionary: "displayMetrics",
  gridPosition: [3, 0],
  tooltipOrder: 3,
};

const axisTitles = {
  xAxisName: {
    default: "",
    type: "text",
    label: "Horizontal Axis Title",
    gridPosition: [0, 1],
    forAxis: "decompositionMetric",
  },
  yAxisName: {
    default: "",
    type: "text",
    label: "Vertical Axis Title",
    gridPosition: [2, 1],
    forAxis: "displayMetric",
  },
};

const stackedColumnLineDecompositionTimeSettings = {
  palette,
  decompositionMetric,
  decompositionMetricSecondary,
  displayMetric: {
    default: "spend",
    type: "select",
    label: "Display Metric (Stacked Bars)",
    dictionary: "displayMetrics",
    gridPosition: [2, 0],
    tooltipOrder: 2,
  },
  lineMetric,
  legendPosition: { ...legendPosition, gridPosition: [4, 0] },
  compactFormat: { ...compactFormat, gridPosition: [5, 0] },
  ...axisTitles,
};

const stackedColumnLineDecompositionTimePlanExploreSettings = {
  ...stackedColumnLineDecompositionTimeSettings,
  showDataLabels: { ...showDataLabels, gridPosition: [6, 0] },
  dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [6, 1] },
  dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [7, 0] },
  dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [7, 1] },
  dataLabelsHideThreshold: { ...dataLabelsHideThreshold, gridPosition: [8, 1] },
  dataLabelsFlipHorizontally: { ...dataLabelsFlipHorizontally, gridPosition: [8, 0] },
};

const stackedColumnLineDecompositionTimePlanSettings = {
  palette,
  decompositionMetric,
  decompositionMetricSecondary,
  displayMetric: {
    default: "impressions",
    type: "select",
    label: "Display Metric (Stacked Bars)",
    dictionary: "displayMetrics",
    gridPosition: [2, 0],
    tooltipOrder: 2,
  },
  lineMetric: { ...lineMetric, default: "channel_budget" },
  legendPosition: { ...legendPosition, gridPosition: [4, 0] },
  compactFormat: { ...compactFormat, gridPosition: [5, 0] },
  showDataLabels: { ...showDataLabels, gridPosition: [7, 0] },
  dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [7, 1] },
  dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [8, 0] },
  dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [8, 1] },
  dataLabelsHideThreshold: { ...dataLabelsHideThreshold, gridPosition: [9, 1] },
  dataLabelsFlipHorizontally: { ...dataLabelsFlipHorizontally, gridPosition: [9, 0] },
  ...axisTitles,
};

const stackedColumnLineDecompositionTimeDualSettings = {
  palette,
  displayMetricSource: { default: "explore", dictionary: "metricSources" },
  lineMetricSource: { default: "plan", dictionary: "metricSources" },
  decompositionMetric: {
    default: "month",
    type: "select",
    label: "Horizontal Axis (Date)",
    dictionary: "timeMetrics",
    apiParameter: "dimension",
    gridPosition: [0, 0],
    tooltipOrder: 0,
  },
  decompositionMetricSecondary: {
    default: "channel",
    type: "select",
    label: "Grouping Dimension (Stacked Bars)",
    dictionary: "dualDimensionMetrics",
    apiParameter: "dimension",
    gridPosition: [1, 0],
    tooltipOrder: 1,
  },
  displayMetric: {
    default: "impressions",
    defaultBySource: { plan: "impressions", explore: "impressions" },
    type: "select",
    label: "Display Metric (Stacked Bars)",
    dictionary: "displayMetrics",
    sourceFrom: "displayMetricSource",
    gridPosition: [2, 0],
    tooltipOrder: 2,
  },
  lineMetric: {
    default: "channel_budget",
    defaultBySource: { plan: "channel_budget", explore: "spend" },
    type: "select",
    label: "Display Metric (Line)",
    dictionary: "displayMetrics",
    sourceFrom: "lineMetricSource",
    gridPosition: [3, 0],
    tooltipOrder: 3,
  },
  legendPosition: { ...legendPosition, gridPosition: [4, 0] },
  showDataLabels: { ...showDataLabels, gridPosition: [5, 0] },
  dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [5, 1] },
  dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [6, 0] },
  dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [6, 1] },
  dataLabelsHideThreshold: { ...dataLabelsHideThreshold, gridPosition: [7, 1] },
  dataLabelsFlipHorizontally: { ...dataLabelsFlipHorizontally, gridPosition: [7, 0] },
  ...axisTitles,
};

export default <Config>{
  family: [FAMILY.OVERTIME],
  name: "stackedColumnLineDecompositionTimeChart",
  title: "Stacked Column & Line Type 1",
  defaultHeight: 6,
  controls: [granularity],
  dataModes: ["project", "explore", "dual"],
  settings: stackedColumnLineDecompositionTimeSettings,
  planSettings: stackedColumnLineDecompositionTimePlanSettings,
  planExploreSettings: stackedColumnLineDecompositionTimePlanExploreSettings,
  planDualSettings: stackedColumnLineDecompositionTimeDualSettings,
};
